import { createContext, useEffect, useState, type PropsWithChildren } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from './firebase.js';

export interface AuthIdentity {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface AuthState {
  user: AuthIdentity | null;
  loading: boolean;
  /**
   * True when VITE_USE_MOCKS=1. UI bypasses the Microsoft popup and the
   * shell renders with a synthetic identity matching the msw `/api/me`
   * mock. Real sign-in is only exercised against the dev backend.
   */
  mocked: boolean;
}

const useMocks = import.meta.env.VITE_USE_MOCKS === '1';

const mockedIdentity: AuthIdentity = {
  uid: 'mock-uid',
  email: 'finance@altyor.com',
  displayName: 'Mock Finance User',
};

export const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  mocked: useMocks,
});

function toIdentity(u: User): AuthIdentity {
  return { uid: u.uid, email: u.email, displayName: u.displayName };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthIdentity | null>(useMocks ? mockedIdentity : null);
  const [loading, setLoading] = useState(!useMocks);

  useEffect(() => {
    if (useMocks) return;
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u ? toIdentity(u) : null);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, mocked: useMocks }}>
      {children}
    </AuthContext.Provider>
  );
}
